import { classifyOption } from '@/lib/menu/classifyModifier'

/**
 * Carrito ↔ menú en vivo. `useKioskMenuSync` refresca el menú cuando el dueño
 * marca un producto en 86 o lo oculta, pero antes el carrito no se enteraba y
 * el ítem se cobraba igual. Esto dice qué líneas ya no se pueden vender: su
 * ítem no está en el menú visible o tiene `is_available === false`. Las líneas
 * sin `item_id` (no existen en el kiosko, pero por si acaso) se conservan.
 *
 * También los INGREDIENTES: una línea armada antes de que el dueño agotara uno
 * de sus ingredientes sale del carrito, y el aviso dice cuál ("Arma tu propia
 * Empanada (Mariscos)"). Hasta 2026-09-13 solo se miraba el plato, y Empanada
 * Factory cobró ingredientes agotados. Una REMOCIÓN no cuenta: "sin cebolla"
 * cuando no hay cebolla es justo lo que la cocina puede hacer — mismo criterio
 * que el checkout online (salta `is_removal`) y que la hoja guiada
 * (`classifyOption` → 'remove'). Una opción que el menú fresco no trae no se da
 * por agotada: no hay dato para afirmarlo.
 */
export interface ReconcileResult {
  removed: Array<{ cart_id: string; name: string }>
}

type MenuOption = {
  id: string
  name?: string | null
  price_delta?: number | null
  is_available?: boolean | null
}

type MenuGroup = {
  display_style?: string | null
  modifier_options?: ReadonlyArray<MenuOption> | null
}

type MenuItem = {
  id: string
  is_available: boolean | null
  item_modifier_groups?: ReadonlyArray<{ modifier_groups?: MenuGroup | null }> | null
}

type CartLine = {
  cart_id: string
  item_id: string | null
  name: string
  modifiers?: ReadonlyArray<{
    modifier_option_id: string
    option_name?: string | null
    is_removal?: boolean | null
  }> | null
}

export function reconcileCart(
  lines: ReadonlyArray<CartLine>,
  items: ReadonlyArray<MenuItem>,
): ReconcileResult {
  const byId = new Map(items.map((i) => [i.id, i]))
  const removed: ReconcileResult['removed'] = []
  for (const line of lines) {
    if (!line.item_id) continue
    const item = byId.get(line.item_id)
    if (!item || item.is_available === false) {
      removed.push({ cart_id: line.cart_id, name: line.name })
      continue
    }
    const soldOut = soldOutIngredients(line, item)
    if (soldOut.length > 0) removed.push({ cart_id: line.cart_id, name: `${line.name} (${soldOut.join(', ')})` })
  }
  return { removed }
}

function findOption(item: MenuItem, optionId: string): { group: MenuGroup; option: MenuOption } | null {
  for (const link of item.item_modifier_groups ?? []) {
    const group = link.modifier_groups
    const option = group?.modifier_options?.find((o) => o.id === optionId)
    if (group && option) return { group, option }
  }
  return null
}

/** Nombres de los ingredientes AGREGADOS de la línea que el menú fresco trae agotados. */
function soldOutIngredients(line: CartLine, item: MenuItem): string[] {
  const names: string[] = []
  for (const m of line.modifiers ?? []) {
    if (m.is_removal === true) continue
    const found = findOption(item, m.modifier_option_id)
    if (!found || found.option.is_available !== false) continue
    const kind = classifyOption(found.group, {
      name: found.option.name ?? '',
      priceDelta: found.option.price_delta ?? 0,
    })
    if (kind === 'remove') continue
    const label = m.option_name || found.option.name || m.modifier_option_id
    if (!names.includes(label)) names.push(label)
  }
  return names
}
